import warningIcon from "$assets/icons/warning-icon.svg?url";
import { Button } from "$components/button/button";
import { DialogBody } from "$components/dialog/dialog-body";
import { DialogContainer } from "$components/dialog/dialog-container";
import { DialogFooter } from "$components/dialog/dialog-footer";
import { DialogHeader } from "$components/dialog/dialog-header";
import { SelectField } from "$components/inputs";
import { toast } from "$global-providers/toast-provider";
import { linkToAddPrices } from "$routes/price-list/links";
import { Box, Grid, makeStyles, Typography } from "@material-ui/core";
import { PriceableItemOrigin } from "elephant-web/gql";
import React, { useMemo, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";
import usePriceTariffsQuery from "./hooks/use-tariffs-query";

const useStyles = makeStyles((theme) => ({
  warningIcon: {
    marginRight: theme.spacing(2),
  },
}));
export interface AddItemsModalProps {
  open: boolean;
  onClose: () => void;
  priceListId: string;
}

export const AddItemsModal = ({
  open,
  onClose,
  priceListId,
}: AddItemsModalProps) => {
  const classes = useStyles();
  const navigate = useNavigate();
  const { formatMessage } = useIntl();
  const [tariffId, setTariffId] = useState<string>();
  const [origin, setOrigin] = useState<PriceableItemOrigin>();
  const { tariffs, loading } = usePriceTariffsQuery();

  const originOptions = useMemo(
    () => [
      {
        value: PriceableItemOrigin.General,
        label: formatMessage({
          id: "priceList.addItems.itemType.general.label",
          defaultMessage: "General",
        }),
      },
      {
        value: PriceableItemOrigin.Inventory,
        label: formatMessage({
          id: "priceList.addItems.itemType.inventory.label",
          defaultMessage: "Inventory",
        }),
      },
      {
        value: PriceableItemOrigin.Investigation,
        label: formatMessage({
          id: "priceList.addItems.itemType.investigation.label",
          defaultMessage: "Investigation",
        }),
      },
      {
        value: PriceableItemOrigin.Procedure,
        label: formatMessage({
          id: "priceList.addItems.itemType.procedure.label",
          defaultMessage: "Procedure",
        }),
      },
      {
        value: PriceableItemOrigin.Custom,
        label: formatMessage({
          id: "prceList.addItems.itemType.custom.label",
          defaultMessage: "Custom",
        }),
      },
    ],
    [formatMessage],
  );

  const tariffOptions =
    tariffs?.map((i) => ({
      value: i.id,
      label: i.name,
    })) ?? [];

  const handleAdd = () => {
    if (!origin || !tariffId) {
      toast.error(
        "Cannot add items without selecting item type and price type.",
      );
      return;
    }
    navigate(linkToAddPrices(priceListId, { origin, tariffId }));
    onClose();
  };

  const tariffLabel = formatMessage({
    id: "pricelist.modal.addItems.reason.label",
    defaultMessage: "Select Price Type",
  });

  const originLabel = formatMessage({
    id: "pricelist.modal.addItems.reason.placeholder",
    defaultMessage: "Select Item Type",
  });

  return (
    <DialogContainer open={open}>
      <DialogHeader title="">
        <Box display="flex" alignItems="center">
          <img
            src={warningIcon}
            alt="warning"
            className={classes.warningIcon}
          />
          <Typography variant="h5">
            <FormattedMessage
              id="pricelist.modal.addItems.modal.title"
              defaultMessage="Add Items"
            />
          </Typography>
        </Box>
      </DialogHeader>
      <DialogBody>
        <Grid container>
          <SelectField
            fullWidth
            label={tariffLabel}
            options={tariffOptions}
            inputLabelProps={{
              id: "tariff",
            }}
            selectProps={{
              disabled: loading,
              value: tariffId,
              "data-testid": "pricelist.add.tariff",

              // @ts-ignore: value is string or unknown
              onChange: (e: React.ChangeEvent<{ value?: string }>) =>
                setTariffId(e.target.value),
            }}
          />
          <SelectField
            fullWidth
            hideHelper
            label={originLabel}
            options={originOptions}
            inputLabelProps={{
              id: "origin",
            }}
            selectProps={{
              value: origin,
              "data-testid": "pricelist.add.origin",

              // @ts-ignore: value is string or unknown
              onChange: (
                e: React.ChangeEvent<{ value?: PriceableItemOrigin }>,
              ) => setOrigin(e.target.value),
            }}
          />
        </Grid>
      </DialogBody>
      <DialogFooter>
        <Button color="secondary" onClick={() => onClose()}>
          <FormattedMessage
            id="pricelist.modal.addItems.cancel"
            defaultMessage="Cancel"
          />
        </Button>
        <Button
          onClick={handleAdd}
          data-testid="pricelist.modal.addItems.accept"
          disabled={!origin || !tariffId}
        >
          <FormattedMessage
            id="pricelist.modal.addItems.confirm"
            defaultMessage="Add Items"
          />
        </Button>
      </DialogFooter>
    </DialogContainer>
  );
};

export default AddItemsModal;
